import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Loader2, Tent } from 'lucide-react';
import { fetchFestivalProgrammePublic } from '../services/api';

/**
 * Le programme public du festival — la page du festivalier.
 *
 * LE BALISAGE PORTE LE SENS, LE CSS FAIT LA GRILLE
 * ---------------------------------------------------------------------------
 * Hiérarchie continue h1 (édition) → h2 (scène) → h3 (artiste), horaires dans
 * des <time datetime> ISO, navigation des jours en vrai <nav> avec
 * aria-current. L'annulation est portée par le TEXTE — « Concert annulé » —
 * jamais par le seul barré ni la seule couleur.
 *
 * Les heures s'affichent dans le fuseau de l'ÉDITION, pas celui du
 * navigateur : le festivalier belge qui vient dans le Sud lit l'heure du
 * site, comme sur l'affiche.
 */
export default function ProgrammeFestivalPage() {
  const { slug = '' } = useParams();
  const [jour, setJour] = useState<string | null>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: ['festival-public', slug],
    queryFn: () => fetchFestivalProgrammePublic(slug),
    enabled: slug !== '',
    staleTime: 60_000,
  });

  const jours = useMemo(() => {
    const vus = new Set<string>();
    for (const p of data?.passages ?? []) {
      vus.add(p.jour);
    }
    return [...vus].sort();
  }, [data]);

  const jourCourant = jour ?? jours[0] ?? null;

  const parZone = useMemo(() => {
    const groupes = new Map<string, NonNullable<typeof data>['passages']>();
    for (const p of data?.passages ?? []) {
      if (jourCourant !== null && p.jour !== jourCourant) continue;
      groupes.set(p.zone, [...(groupes.get(p.zone) ?? []), p]);
    }
    return groupes;
  }, [data, jourCourant]);

  if (isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="p-4 lg:p-0">
        <p className="card text-sm text-gray-600 dark:text-gray-400">
          Ce programme n'est pas (ou pas encore) publié.
        </p>
      </div>
    );
  }

  const heure = (iso: string) =>
    new Date(iso).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: data.edition.fuseau,
    });

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Tent className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Programme — {data.edition.nom}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Programme susceptible de modification.{' '}
            <a
              href={`/api/festival/public/${data.edition.slug}/programme.ics`}
              className="underline text-primary-700 dark:text-primary-300"
            >
              Ajouter à mon agenda (ICS)
            </a>
          </p>
        </div>
      </div>

      {jours.length > 1 && (
        <nav aria-label="Choix du jour">
          <ul className="flex gap-2 flex-wrap">
            {jours.map((j) => (
              <li key={j}>
                <button
                  type="button"
                  onClick={() => setJour(j)}
                  aria-current={j === jourCourant ? 'page' : undefined}
                  className={`flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm capitalize transition-colors ${
                    j === jourCourant
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200'
                      : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary-300'
                  }`}
                >
                  <CalendarDays className="w-4 h-4" />
                  {new Date(`${j}T12:00:00Z`).toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {data.zones
        .filter((z) => parZone.has(z.code))
        .map((zone) => (
          <section key={zone.code} className="card space-y-2" aria-labelledby={`zone-${zone.code}`}>
            <h2 id={`zone-${zone.code}`} className="font-semibold text-gray-900 dark:text-gray-100">
              {zone.libelle}
            </h2>
            {zone.notesAccessibilite && (
              <p className="text-xs text-gray-500 dark:text-gray-400">♿ {zone.notesAccessibilite}</p>
            )}
            <ol className="divide-y divide-gray-100 dark:divide-gray-700">
              {(parZone.get(zone.code) ?? []).map((p, i) => (
                <li key={i} className="py-2 flex items-baseline gap-3">
                  <p className="tabular-nums text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    <time dateTime={p.debut}>{heure(p.debut)}</time>
                    {' – '}
                    <time dateTime={p.fin}>{heure(p.fin)}</time>
                  </p>
                  <h3
                    className={`font-medium text-gray-900 dark:text-gray-100 ${
                      p.annule ? 'line-through' : ''
                    }`}
                  >
                    {p.titre}
                  </h3>
                  {p.annule && (
                    <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                      Concert annulé
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </section>
        ))}

      {(data.passages.length === 0 || parZone.size === 0) && (
        <p className="card text-sm text-gray-600 dark:text-gray-400">
          Le programme n'est pas encore annoncé — revenez bientôt.
        </p>
      )}
    </div>
  );
}
